import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { resolveArticleAdConfig } from '@/lib/articleAdResolver';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { ArrowRight, Sparkles, TrendingUp } from 'lucide-react';
import { trackAffiliateClick } from '@/lib/analytics';
import { trackAffiliateClickGTM } from '@/lib/gtm';
import { useAffiliateRedirect } from '@/hooks/useAffiliateRedirect';
import { CardLogo } from './CardLogo';
import { Link } from "@/lib/router-compat";
import { useFeedSettings } from '@/hooks/useFeedSettings';

interface AdCard {
  id: string;
  name: string;
  slug: string;
  issuer: string;
  logo_url: string;
  annual_fee: number;
  apr_effective: number;
  affiliate_link: string;
  selling_point_1?: string;
  selling_point_2?: string;
  show_tag?: boolean;
}

interface AdConfig {
  header_text: string;
  footer_text: string | null;
  header_tag: string | null;
  max_cards: number;
  selection_mode: string;
}

interface SponsoredCardsModuleProps {
  articleId: string;
  categoryId?: string;
}

export function SponsoredCardsModule({ articleId, categoryId }: SponsoredCardsModuleProps) {
  const [cards, setCards] = useState<AdCard[]>([]);
  const [config, setConfig] = useState<AdConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const { data: feedSettings } = useFeedSettings();
  const { handleAffiliateRedirect } = useAffiliateRedirect();
  
  // Default German translations
  const [translations, setTranslations] = useState({
    ad_badge: 'Anzeige',
    popular_badge: 'Am beliebtesten',
    annual_fee_label: 'Jahresgebühr',
    effective_rate_label: 'Eff. Zinssatz',
    free_label: 'Gratis',
    cta_button: 'Jetzt beantragen',
    trust_line_template: 'Sichere Online-Antragstellung über {issuer}. Antwort in wenigen Minuten.',
    legal_line_template: 'Eff. Jahreszins: {aprPercent} % · {amount} € · {months} Monate · Gesamtkosten {total} €'
  });

  // Load translations from feed settings
  useEffect(() => {
    if (feedSettings?.sponsored_cards_translations) {
      setTranslations(feedSettings.sponsored_cards_translations as typeof translations);
    }
  }, [feedSettings]);

  // Helper function for German number formatting
  const formatGermanNumber = (num: number, decimals = 2): string => {
    return num.toFixed(decimals).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  };

  // Helper function to generate trust line
  const generateTrustLine = (issuer: string): string => {
    return translations.trust_line_template.replace('{issuer}', issuer);
  };

  // Helper function to generate legal line
  const generateLegalLine = (card: AdCard): string => {
    const amount = 1500; // Fixed example amount in EUR
    const months = 12;
    const aprPercent = formatGermanNumber(card.apr_effective, 2);
    const total = Math.round(amount + (amount * card.apr_effective / 100));
    
    return translations.legal_line_template
      .replace('{aprPercent}', aprPercent)
      .replace('{amount}', amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' '))
      .replace('{months}', months.toString())
      .replace('{total}', total.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' '));
  };

  useEffect(() => {
    async function fetchAdData() {
      try {
        // Resolve which config to use
        const configId = await resolveArticleAdConfig(articleId, categoryId);
        if (!configId) {
          setLoading(false);
          return;
        }

        // Fetch config details
        const { data: configData, error: configError } = await supabase
          .from('article_ad_configs')
          .select('*')
          .eq('id', configId)
          .single();

        if (configError) throw configError;
        setConfig(configData as AdConfig);

        // Fetch cards based on selection mode
        let cardsData: AdCard[] = [];

        if (configData.selection_mode === 'manual') {
          // Manual mode: Get cards from overrides table
          const { data, error } = await supabase
            .from('article_ad_card_overrides')
            .select(`
              selling_point_1,
              selling_point_2,
              show_tag,
              display_order,
              cards!inner(
                id,
                name,
                slug,
                issuer,
                logo_url,
                annual_fee,
                apr_effective,
                affiliate_link,
                status
              )
            `)
            .eq('config_id', configId)
            .eq('cards.status', 'published')
            .order('display_order', { ascending: true })
            .limit(configData.max_cards!);

          if (error) throw error;
          
          cardsData = data?.map((item: any) => ({
            id: item.cards.id,
            name: item.cards.name,
            slug: item.cards.slug,
            issuer: item.cards.issuer,
            logo_url: item.cards.logo_url,
            annual_fee: item.cards.annual_fee,
            apr_effective: item.cards.apr_effective,
            affiliate_link: item.cards.affiliate_link,
            selling_point_1: item.selling_point_1,
            selling_point_2: item.selling_point_2,
            show_tag: item.show_tag,
          })) || [];

        } else if (configData.selection_mode === 'random_promoted') {
          // Random from promoted ads
          const now = new Date().toISOString();
          const activeWindow = [
            `and(ad_placements.start_at.is.null,ad_placements.end_at.is.null)`,
            `and(ad_placements.start_at.is.null,ad_placements.end_at.gte.${now})`,
            `and(ad_placements.start_at.lte.${now},ad_placements.end_at.is.null)`,
            `and(ad_placements.start_at.lte.${now},ad_placements.end_at.gte.${now})`,
          ].join(',');
          const { data, error } = await supabase
            .from('cards')
            .select(`
              id,
              name,
              slug,
              issuer,
              logo_url,
              annual_fee,
              apr_effective,
              affiliate_link,
              selling_points,
              ad_placements!inner(card_id)
            `)
            .eq('status', 'published')
            .or(activeWindow, { referencedTable: 'ad_placements' })
            .limit(configData.max_cards! * 3); // Fetch more for randomization

          if (error) throw error;

          // Shuffle and take max_cards
          const shuffled = data?.sort(() => Math.random() - 0.5) || [];
          cardsData = shuffled.slice(0, configData.max_cards ?? undefined).map((card: any) => ({
            id: card.id,
            name: card.name,
            slug: card.slug,
            issuer: card.issuer,
            logo_url: card.logo_url,
            annual_fee: card.annual_fee,
            apr_effective: card.apr_effective,
            affiliate_link: card.affiliate_link,
            selling_point_1: card.selling_points?.[0] || '',
            selling_point_2: card.selling_points?.[1] || '',
            show_tag: false,
          }));

        } else if (configData.selection_mode === 'top_priority') {
          // Top priority from ad placements
          const now = new Date().toISOString();
          const activeWindow = [
            `and(start_at.is.null,end_at.is.null)`,
            `and(start_at.is.null,end_at.gte.${now})`,
            `and(start_at.lte.${now},end_at.is.null)`,
            `and(start_at.lte.${now},end_at.gte.${now})`,
          ].join(',');
          const { data, error } = await supabase
            .from('ad_placements')
            .select(`
              priority,
              cards!inner(
                id,
                name,
                slug,
                issuer,
                logo_url,
                annual_fee,
                apr_effective,
                affiliate_link,
                selling_points,
                status
              )
            `)
            .eq('cards.status', 'published')
            .or(activeWindow)
            .order('priority', { ascending: false })
            .limit(configData.max_cards!);

          if (error) throw error;

          cardsData = data?.map((item: any) => ({
            id: item.cards.id,
            name: item.cards.name,
            slug: item.cards.slug,
            issuer: item.cards.issuer,
            logo_url: item.cards.logo_url,
            annual_fee: item.cards.annual_fee,
            apr_effective: item.cards.apr_effective,
            affiliate_link: item.cards.affiliate_link,
            selling_point_1: item.cards.selling_points?.[0] || '',
            selling_point_2: item.cards.selling_points?.[1] || '',
            show_tag: false,
          })) || [];
        }

        setCards(cardsData);
      } catch (error) {
        console.error('Error fetching article ad data:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchAdData();
  }, [articleId, categoryId]);

  if (loading || !config || cards.length === 0) {
    return null;
  }

  const handleCardClick = async (card: AdCard) => {
    if (card.affiliate_link) {
      trackAffiliateClick(card.id, 'article_sidebar');
      
      // Wait for GTM event to be sent
      await trackAffiliateClickGTM({
        cardId: card.id,
        cardName: card.name,
        issuerName: card.issuer,
        affiliateUrl: card.affiliate_link,
        clickSource: 'article_sidebar',
        annualFee: card.annual_fee,
        effectiveRate: card.apr_effective,
      });
      
      // Now navigate
      handleAffiliateRedirect({
        cardId: card.id,
        cardName: card.name,
        issuerName: card.issuer,
        affiliateUrl: card.affiliate_link,
        source: 'article_sidebar',
        annualFee: card.annual_fee,
        effectiveRate: card.apr_effective,
      });
    }
  };

  return (
    <div className="sticky top-24 space-y-3">
      {/* Module header */}
      <div className="flex items-center justify-between px-1">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-primary" />
           <span className="text-sm text-muted-foreground">{config.header_text}</span>
         </div>
         <Badge variant="outline" className="text-xs">
           {translations.ad_badge}
         </Badge>
       </div>

      {/* Cards */}
      <div className="space-y-3">
        {cards.map((card, index) => (
          <Card 
            key={card.id} 
            className="overflow-hidden border-primary/20 hover:border-primary/40 transition-all duration-300 hover:shadow-lg group"
          >
            {/* Gradient accent bar */}
            <div className="h-1 bg-gradient-to-r from-primary to-primary/80" />
            
            <div className="p-5 space-y-4">
              {/* Bank logo & name */}
              <Link to={`/kreditkarten/${card.slug}`} className="block hover:opacity-90 transition-opacity">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <CardLogo 
                        logoUrl={card.logo_url}
                        bankName={card.issuer}
                        className="w-16 h-10"
                      />
                      {config.header_tag && (card.show_tag || index === 0) && (
                        <Badge className="bg-gradient-to-r from-primary to-primary/80 text-primary-foreground border-0 text-xs px-2.5 py-0.5">
                          {config.header_tag}
                        </Badge>
                      )}

                    </div>
                    <h3 className="text-foreground mb-0.5 truncate font-semibold">
                      {card.name}
                    </h3>
                  </div>
                </div>
              </Link>

              {/* Key selling points - max 2 */}
              {(card.selling_point_1 || card.selling_point_2) && (
                <div className="space-y-2">
                  {card.selling_point_1 && (
                    <div className="flex items-start gap-2">
                      <TrendingUp className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                      <span className="text-sm text-foreground leading-snug">{card.selling_point_1}</span>
                    </div>
                  )}
                  {card.selling_point_2 && (
                    <div className="flex items-start gap-2">
                      <TrendingUp className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                      <span className="text-sm text-foreground leading-snug">{card.selling_point_2}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Highlight info */}
              <div className="grid grid-cols-2 gap-3 pt-3 border-t">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">{translations.annual_fee_label}</p>
                  <p className="text-foreground font-medium">
                    {card.annual_fee === 0 ? (
                      <span className="text-green-600">{translations.free_label}</span>
                    ) : (
                      `${formatGermanNumber(card.annual_fee, 0)} €`
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">{translations.effective_rate_label}</p>
                  <p className="text-foreground font-medium">{formatGermanNumber(card.apr_effective, 2)} %</p>
                </div>
              </div>

              {/* CTA Button */}
              <Button 
                className="w-full bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-primary-foreground group-hover:shadow-md transition-all"
                onClick={() => handleCardClick(card)}
              >
                {translations.cta_button}
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-0.5 transition-transform" />
              </Button>

              {/* Trust line */}
              <p className="text-xs text-muted-foreground text-center leading-tight">
                {generateTrustLine(card.issuer)}
              </p>

              {/* Small disclaimer */}
              <p className="text-xs text-muted-foreground text-center leading-tight">
                {generateLegalLine(card)}
              </p>
            </div>
          </Card>
        ))}
      </div>

      {/* Footer note */}
      {config.footer_text && (
        <p className="text-xs text-muted-foreground text-center px-2 leading-relaxed">
          {config.footer_text}
        </p>
      )}
    </div>
  );
}
